import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Loader2, Lock, ShieldAlert, Stamp, Users2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MoneyInput } from "@/components/common/MoneyInput";
import { CapacidadPagoComite } from "@/components/solicitudes/CapacidadPagoComite";
import VistaPreviaComite from "@/components/solicitudes/VistaPreviaComite";
import { ExpedienteComite } from "@/components/solicitudes/comite/ExpedienteComite";
import { FactorajeResumen } from "@/components/solicitudes/FactorajeResumen";
import { ArrendamientoResumen } from "@/components/solicitudes/ArrendamientoResumen";
import { MarcaMigracionComite } from "@/components/migracion/MarcaMigracionComite";
import { NOTA_IVA_RENTAS, NOTA_PROYECCION } from "@/lib/arrendamiento";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AvisosDatosFiscales } from "@/components/fiscal/AvisosDatosFiscales";
import { api, apiError } from "@/lib/api";
import { mxn, fdate, formatearPlazo } from "@/lib/format";
import { estaSellada, esPendienteComite } from "@/lib/selloSolicitud";
import { toast } from "sonner";

const DECISIONES = [
  ["Aprobado", "Autoriza la solicitud: pasa a 'Autorizado' para contratar"],
  ["Rechazado", "Rechaza la solicitud (queda cerrada)"],
  ["Condicionado", "Autoriza con condiciones (se documentan en el comentario)"],
  ["Más documentos", "Regresa a análisis: falta documentación del cliente"],
  ["Nueva consulta", "Regresa a análisis: se requiere otra consulta SIC"],
  ["Revisión PLD", "Envía a Cumplimiento para revisión PLD antes de decidir"],
];

// Factoraje (Build 1): una sola cuota al vencimiento de la factura; el plazo técnico es 1.
const plazoInicial = (d) => (d.es_factoraje ? "1" : Number(d.plazo_autorizado) > 0
  ? String(d.plazo_autorizado) : (Number(d.plazo) > 0 ? String(d.plazo) : ""));

export default function ComiteCredito() {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [comentario, setComentario] = useState("");
  const [justificacion, setJustificacion] = useState("");
  const [destino, setDestino] = useState("");
  const [busy, setBusy] = useState(false);
  // oct-2026 · El MONTO AUTORIZADO lo fija el Comité al decidir (ya no lo captura el
  // ejecutivo). Arranca con la propuesta del ejecutivo si la solicitud venía con una.
  const [montoAut, setMontoAut] = useState("");
  // oct-2026 · El PLAZO AUTORIZADO se fija en el mismo acto, precargado con el solicitado.
  const [plazoAut, setPlazoAut] = useState("");

  const cargar = useCallback(async () => {
    try {
      setData(await api.get("/comite/bandeja").then((r) => r.data));
    } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const abrir = async (row) => {
    setComentario(""); setJustificacion(""); setDestino(""); setMontoAut(""); setPlazoAut("");
    try {
      const d = await api.get(`/comite/solicitudes/${row.id}`).then((r) => r.data);
      setSel(d);
      setMontoAut(Number(d.monto_propuesto) > 0 ? String(d.monto_propuesto) : "");
      setPlazoAut(plazoInicial(d));
    } catch (e) { toast.error(apiError(e)); }
  };

  const EXIGE_MONTO = ["Aprobado", "Condicionado"];

  const decidir = async (decision) => {
    const pideMonto = EXIGE_MONTO.includes(decision);
    const solicitado = Number(sel.monto_solicitado ?? sel.monto ?? 0);
    const n = Number(montoAut);
    const p = Number(plazoAut);
    const plazoSol = Number(sel.plazo ?? 0);
    const permitidos = (sel.producto_plazos_permitidos || []).map(Number);
    if (pideMonto) {
      if (!montoAut || !Number.isFinite(n) || n <= 0) {
        toast.error("Captura el monto autorizado: lo fija el Comité al aprobar o condicionar.");
        return;
      }
      const anticipo = Number(sel.factoraje?.anticipo);
      if (sel.es_factoraje && anticipo > 0 && n > anticipo) {
        toast.error(`Factoraje: el monto autorizado (${mxn(n)}) no puede ser mayor al anticipo de la factura (${mxn(anticipo)}).`);
        return;
      }
      const capital = Number(sel.arrendamiento?.capital_arrendar);
      if (sel.es_arrendamiento && capital > 0 && n > capital) {
        toast.error(`Arrendamiento: el monto autorizado (${mxn(n)}) no puede ser mayor al capital a arrendar (${mxn(capital)}).`);
        return;
      }
      if (solicitado > 0 && n > solicitado) {
        toast.error(`El monto autorizado (${mxn(n)}) no puede ser mayor al solicitado (${mxn(solicitado)}). El Comité puede autorizar menos, nunca más.`);
        return;
      }
      const mn = sel.producto_monto_min, mx = sel.producto_monto_max;
      if ((mn != null && n < Number(mn)) || (mx != null && n > Number(mx))) {
        toast.error(`El monto autorizado (${mxn(n)}) queda fuera del rango del producto: ${mxn(mn)} – ${mxn(mx)}.`);
        return;
      }
      if (!plazoAut || !Number.isFinite(p) || p <= 0) {
        toast.error("Captura el plazo autorizado: lo fija el Comité al aprobar o condicionar, igual que el monto.");
        return;
      }
      if (permitidos.length && !permitidos.includes(p)) {
        toast.error(`El plazo ${p} no está entre los plazos permitidos del producto: ${permitidos.join(", ")}.`);
        return;
      }
      if (plazoSol > 0 && p !== plazoSol && comentario.trim().length < 10) {
        toast.error(`Cambias el plazo de ${plazoSol} a ${p} pagos: el comentario es obligatorio para explicar el cambio (mínimo 10 caracteres).`);
        return;
      }
    }
    setBusy(true);
    try {
      const { data: res } = await api.post("/comite/decidir", {
        solicitud_id: sel.id, decision, comentarios: comentario,
        justificacion,
        ...(pideMonto ? { monto_autorizado: n, plazo_autorizado: p } : {}),
      });
      toast.success(`Decisión registrada: ${decision}`
        + (res.monto_autorizado ? ` · Monto autorizado ${mxn(res.monto_autorizado)} (sellado)` : "")
        + (res.plazo_autorizado ? ` · Plazo autorizado ${formatearPlazo(res.plazo_autorizado, sel.periodicidad)}` : "")
        + (res.autorizacion_propia ? " · Autorización propia" : ""));
      const d = await api.get(`/comite/solicitudes/${sel.id}`).then((r) => r.data);
      setSel(d);
      setComentario(""); setJustificacion("");
      setMontoAut(Number(d.monto_propuesto) > 0 ? String(d.monto_propuesto) : "");
      setPlazoAut(plazoInicial(d));
      cargar();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const reasignar = async () => {
    if (!destino) return;
    setBusy(true);
    try {
      await api.post("/comite/asignar", { solicitud_id: sel.id, usuario_id: destino });
      toast.success("Solicitud reasignada");
      setSel(await api.get(`/comite/solicitudes/${sel.id}`).then((r) => r.data));
      cargar();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const repartir = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/comite/reparto", {});
      toast.success(`Reparto automático: ${res.asignadas} solicitud(es) asignada(s)`);
      cargar();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const filas = data?.solicitudes || [];
  // sep-2026 · El Comité solo decide lo PENDIENTE y ya ENVIADO (sellado). Sin sello no hay
  // decisión: el backend responde 409 y aquí los botones nacen deshabilitados.
  const selSellada = estaSellada(sel);
  const puedeDecidir = !!sel && selSellada && esPendienteComite(sel);
  const fmtPlazo = (p) => (sel?.es_factoraje
    ? `Una sola cuota · vence ${sel.factoraje?.fecha_vencimiento_factura || "—"}`
    : formatearPlazo(p, sel?.periodicidad));

  return (
    <div>
      <PageHeader title="Comité de Crédito"
        subtitle="Bandeja de decisión · autorización con cuatro ojos e historial por solicitud" />
      <div className="space-y-4 p-6 md:p-8">
        <div className="flex flex-wrap items-center gap-3">
          <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Pendientes de decisión</p>
            <p data-testid="comite-contador-pendientes" className="font-heading text-lg font-semibold text-slate-800">
              {data?.pendientes ?? "—"}
            </p>
          </div>
          <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">En mi bandeja</p>
            <p data-testid="comite-contador-bandeja" className="font-heading text-lg font-semibold text-slate-800">
              {filas.length}
            </p>
          </div>
          {data?.puede_asignar && (
            <>
              <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">Sin asignar</p>
                <p data-testid="comite-contador-sin-asignar" className="font-heading text-lg font-semibold text-slate-800">
                  {data?.sin_asignar ?? 0}
                </p>
              </div>
              <Button size="sm" variant="outline" data-testid="comite-reparto-btn"
                disabled={busy} onClick={repartir}>
                <Users2 className="mr-1.5 h-4 w-4" /> Repartir pendientes
              </Button>
            </>
          )}
          {data?.asignacion_origen === "fallback_admin" && (
            <p data-testid="comite-aviso-sin-analistas" className="text-xs text-amber-700">
              La entidad no tiene analistas del Comité: las solicitudes quedan en la bandeja del
              Administrador.
            </p>
          )}
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm" data-testid="comite-bandeja">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                {["Cliente", "Producto", "Monto solicitado", "Monto autorizado",
                  "Plazo solicitado", "Plazo autorizado", "Estatus", "Asignada a",
                  "Última decisión"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400">
                  Sin solicitudes en tu bandeja.
                </td></tr>
              )}
              {filas.map((s) => (
                <tr key={s.id} data-testid={`comite-fila-${s.id}`}
                  onClick={() => abrir(s)}
                  className="cursor-pointer border-t border-slate-100 hover:bg-forest-600/5">
                  <td className="px-3 py-2 font-medium text-slate-800">{s.cliente || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{s.producto || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{mxn(s.monto_solicitado ?? s.monto)}</td>
                  <td className="px-3 py-2 text-slate-600"
                    data-testid={`comite-fila-monto-aut-${s.id}`}>
                    {Number(s.monto_autorizado) > 0 ? mxn(s.monto_autorizado)
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{formatearPlazo(s.plazo, s.periodicidad)}</td>
                  <td className="px-3 py-2 text-slate-600"
                    data-testid={`comite-fila-plazo-aut-${s.id}`}>
                    {Number(s.plazo_autorizado) > 0
                      ? formatearPlazo(s.plazo_autorizado, s.periodicidad)
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <StatusBadge value={s.estatus} />
                      {s.pendiente && (s.sellada ? (
                        <span data-testid={`comite-sellada-${s.id}`}
                          title="Condiciones selladas al enviarla al Comité"
                          className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                          <Lock className="h-3 w-3" /> Sellada
                        </span>
                      ) : (
                        <span data-testid={`comite-sin-enviar-${s.id}`}
                          title="El ejecutivo aún no pulsa «Enviar al Comité»"
                          className="rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                          Sin enviar
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{s.comite_asignado_nombre || "Sin asignar"}</td>
                  <td className="px-3 py-2 text-slate-600">
                    {s.comite?.decision
                      ? <span>{s.comite.decision}{s.comite.autorizacion_propia ? " · Autorización propia" : ""}</span>
                      : <span className="text-slate-400">Sin decisión</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
        <p className="text-xs text-slate-400">
          Haz clic en una solicitud para registrar la decisión del Comité. Toda decisión queda en el
          historial y en la bitácora.
        </p>
      </div>

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
          {sel && (
            <div className="space-y-4" data-testid="comite-detalle">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-heading">
                  <Stamp className="h-5 w-5 text-primary" /> {sel.cliente || "—"} — {sel.producto || "—"}
                </DialogTitle>
                <DialogDescription className="sr-only">Decisión del Comité de Crédito</DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[["Monto solicitado", mxn(sel.monto_solicitado ?? sel.monto)],
                  ["Plazo solicitado", fmtPlazo(sel.plazo)],
                  ["Plazo autorizado", Number(sel.plazo_autorizado) > 0
                    ? fmtPlazo(sel.plazo_autorizado)
                    : "— lo fija el Comité"],
                  ["Estatus", sel.estatus],
                  ["Capturó", sel.created_by || "—"]].map(([l, v]) => (
                  <div key={l} className="rounded-md border border-slate-200 bg-slate-50 p-2.5">
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">{l}</p>
                    <p className="mt-0.5 text-sm font-semibold text-slate-800">{v}</p>
                  </div>
                ))}
              </div>

              {Array.isArray(sel.referencias_personales) && sel.referencias_personales.some((r) => r?.nombre) && (
                <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="comite-referencias">
                  <p className="mb-2 font-heading text-sm font-semibold text-slate-800">Referencias personales</p>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    {sel.referencias_personales.filter((r) => r?.nombre).map((r, i) => (
                      <div key={i} className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs">
                        <p className="font-semibold text-slate-800">{r.nombre}</p>
                        <p className="text-slate-600">{r.telefono || "Sin teléfono"}{r.ocupacion ? ` · ${r.ocupacion}` : ""}</p>
                        {r.domicilio && <p className="text-slate-500">{r.domicilio}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-lg border border-slate-200 bg-white p-3"
                data-testid="comite-condiciones-selladas">
                <div className="mb-2 flex items-center gap-2">
                  <Lock className={"h-4 w-4 " + (selSellada ? "text-emerald-600" : "text-amber-600")} />
                  <p className="font-heading text-sm font-semibold text-slate-800">
                    {selSellada ? "Condiciones selladas" : "Condiciones sin sellar"}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {[["Monto autorizado", sel.monto_autorizado != null ? mxn(sel.monto_autorizado) : "— lo fija el Comité"],
                    ["Plazo autorizado", Number(sel.plazo_autorizado) > 0
                      ? fmtPlazo(sel.plazo_autorizado)
                      : "— lo fija el Comité"],
                    ["Tasa ordinaria", sel.tasa_ordinaria != null ? `${sel.tasa_ordinaria}%` : "—"],
                    ["Tasa moratoria", sel.tasa_moratoria != null ? `${sel.tasa_moratoria}%` : "—"],
                    ["Comisión por apertura", sel.comision_apertura_tipo === "monto"
                      ? mxn(sel.comision_apertura)
                      : (sel.comision_apertura != null ? `${sel.comision_apertura}%` : "—")],
                    ["IVA", sel.iva != null ? `${sel.iva}%` : "—"],
                    ["Periodicidad", sel.es_factoraje ? "Pago único al vencimiento" : (sel.periodicidad || "—")]].map(([l, v]) => (
                    <div key={l} className="rounded-md border border-slate-100 bg-slate-50 p-2">
                      <p className="text-[10px] uppercase tracking-wide text-slate-500">{l}</p>
                      <p className="mt-0.5 text-sm font-semibold text-slate-800">{v}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-slate-500">
                  {selSellada ? (
                    <>Selladas el {String(sel.sello?.enviada_en || "").slice(0, 10)}
                      {sel.sello?.enviada_por ? ` por ${sel.sello.enviada_por}` : ""} · huella{" "}
                      <span className="font-mono">{sel.sello?.hash || "—"}</span>. El Comité{" "}
                      <b>fija el monto y el plazo autorizados</b> al decidir (se vuelve a
                      huellar); <b>no modifica</b> tasas, comisión, IVA ni periodicidad. Para
                      cambiar esas hay que cancelar la solicitud y capturar una nueva.</>
                  ) : (
                    <>Estas condiciones todavía se pueden capturar: se sellan cuando el ejecutivo
                      pulse «Enviar al Comité».</>
                  )}
                </p>
              </div>

              {sel.es_factoraje && (
                <FactorajeResumen factoraje={sel.factoraje} exposicion={sel.factoraje_exposicion} testid="comite-factoraje" />
              )}
              {sel.es_arrendamiento && (
                <div className="space-y-1.5">
                  {sel.arrendamiento?.es_prorroga && (
                    <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="comite-prorroga-aviso">
                      <b>Prórroga del arrendamiento {sel.arrendamiento.origen_numero_credito}.</b> Financia el residual sin IVA (el IVA se
                      causa en las nuevas rentas). Se autorizan el monto y el plazo capturados; al autorizar, el nuevo crédito se contrata
                      solo (sin desembolso) y el original queda Liquidado con residual «Prorrogada».
                      {sel.arrendamiento.poliza && <> Póliza: {sel.arrendamiento.poliza.aseguradora} · {sel.arrendamiento.poliza.numero_poliza} ·
                        vigente al {fdate(sel.arrendamiento.poliza.vigencia_hasta)}.</>}
                    </div>
                  )}
                  <ArrendamientoResumen arrendamiento={sel.arrendamiento} testid="comite-arrendamiento" />
                  <p className="text-[11px] text-slate-500" data-testid="comite-arrendamiento-notas">{NOTA_PROYECCION} {NOTA_IVA_RENTAS}</p>
                </div>
              )}

              {Array.isArray(sel.datos_faltantes) && sel.datos_faltantes.length > 0 && (
                <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3"
                  data-testid="comite-datos-faltantes">
                  <div className="mb-1 flex items-center gap-2 text-rose-800">
                    <AlertTriangle className="h-4 w-4" />
                    <p className="font-heading text-sm font-semibold">Datos faltantes para autorizar</p>
                  </div>
                  {sel.datos_faltantes.map((b) => (
                    <ul key={`cdf-${b.cliente_id}`} className="list-inside list-disc text-[13px] text-rose-800">
                      {sel.expediente_comite?.tipo === "grupal" && (
                        <li className="list-none font-medium" data-testid={`comite-datos-faltantes-${b.cliente_id}`}>{b.cliente}</li>
                      )}
                      {(b.problemas || []).map((p, i) => <li key={`cdf-p-${i}`}>{p}</li>)}
                    </ul>
                  ))}
                </div>
              )}

              <AvisosDatosFiscales avisos={sel.avisos_fiscales} testid="comite-avisos-fiscales" />

              {sel.exige_justificacion && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3"
                  data-testid="comite-aviso-autorizacion-propia">
                  <div className="mb-1 flex items-center gap-2 text-amber-900">
                    <ShieldAlert className="h-4 w-4" />
                    <p className="font-heading text-sm font-semibold">Autorización propia</p>
                  </div>
                  <p className="text-[13px] text-amber-900">
                    Tú capturaste esta solicitud. Como Administrador de la entidad puedes decidirla,
                    pero la justificación es obligatoria y quedará marcada en el historial y en la
                    bitácora.
                  </p>
                  <Textarea data-testid="comite-justificacion" className="mt-2 bg-white"
                    placeholder="Justifica por qué autorizas tu propia captura (mínimo 10 caracteres)..."
                    value={justificacion} onChange={(e) => setJustificacion(e.target.value)} />
                </div>
              )}
              {sel.capturada_por_mi && !sel.exige_justificacion && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-[13px] text-rose-800"
                  data-testid="comite-bloqueo-cuatro-ojos">
                  Cuatro ojos: tú capturaste esta solicitud, así que no puedes decidirla. Pide la
                  decisión a otro miembro del Comité de Crédito.
                </div>
              )}

              {/* oct-2026 · Capacidad de pago (solo lectura) ARRIBA de la decisión: los
                  documentos de ingresos del solicitante y los datos capturados para
                  evaluarla. El Comité solo ve: no sube, no reemplaza y no borra. */}
              <ExpedienteComite expediente={sel.expediente_comite} />
              <CapacidadPagoComite capacidad={sel.capacidad_pago} />

              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="mb-2 font-heading text-sm font-semibold text-slate-800">Decisión del Comité</p>
                {!puedeDecidir && (
                  <div data-testid="comite-bloqueo-sello"
                    className="mb-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
                    {selSellada ? (
                      <>Esta solicitud ya está «{sel.estatus}»: el Comité no la vuelve a decidir.
                        Para cambiar sus condiciones hay que cancelarla y capturar una nueva.</>
                    ) : (
                      <>Esta solicitud <b>todavía no se envía al Comité</b>. El ejecutivo debe
                        pulsar «Enviar al Comité» en Solicitudes de crédito: ahí quedan selladas
                        las condiciones que el Comité aprueba o rechaza.</>
                    )}
                  </div>
                )}
                {/* oct-2026 · El monto autorizado lo FIJA el Comité al aprobar o condicionar.
                    El monto solicitado va a la vista como referencia y, si la solicitud venía
                    con un monto capturado por el ejecutivo, se muestra como PROPUESTA. */}
                {puedeDecidir && (
                  <div className="mb-3 rounded-md border border-sky-200 bg-sky-50 p-3"
                    data-testid="comite-monto-autorizado-bloque">
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="min-w-[200px] flex-1">
                        <Label className="text-[11px] font-semibold text-sky-900">
                          Monto autorizado <span className="text-rose-600">*</span>
                        </Label>
                        <MoneyInput data-testid="comite-monto-autorizado"
                          value={montoAut} onChange={setMontoAut} disabled={busy} />
                      </div>
                      <div className="text-[11px] text-sky-900">
                        <p>Solicitado: <b data-testid="comite-monto-solicitado-ref">{mxn(sel.monto_solicitado ?? sel.monto)}</b></p>
                        {sel.es_factoraje && (
                          <p className="mt-0.5" data-testid="comite-factoraje-tope">Máximo: anticipo de la factura <b>{mxn(sel.factoraje?.anticipo)}</b></p>
                        )}
                        {sel.es_arrendamiento && (
                          <p className="mt-0.5" data-testid="comite-arrendamiento-tope">Máximo: capital a arrendar <b>{mxn(sel.arrendamiento?.capital_arrendar)}</b></p>
                        )}
                        {(sel.producto_monto_min != null || sel.producto_monto_max != null) && (
                          <p className="mt-0.5">Rango del producto: {mxn(sel.producto_monto_min)} – {mxn(sel.producto_monto_max)}</p>
                        )}
                      </div>
                    </div>
                    {Number(sel.monto_propuesto) > 0 && (
                      <p className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900"
                        data-testid="comite-monto-propuesta">
                        <b>Propuesta del ejecutivo: {mxn(sel.monto_propuesto)}.</b> Es solo una
                        propuesta: el monto que se sella es el que confirmes o cambies aquí.
                      </p>
                    )}
                    {sel.es_factoraje ? (
                      <p className="mt-3 rounded border border-indigo-200 bg-indigo-50 px-2 py-1.5 text-[11px] text-indigo-900"
                        data-testid="comite-factoraje-plazo">
                        Factoraje: <b>una sola cuota</b> al vencimiento de la factura
                        ({sel.factoraje?.fecha_vencimiento_factura || "—"}). No hay plazo ni periodicidad que autorizar.
                      </p>
                    ) : (<>
                    {/* oct-2026 · PLAZO AUTORIZADO, en el mismo acto que el monto. Selector
                        cerrado con los plazos del producto (misma periodicidad). */}
                    <div className="mt-3 flex flex-wrap items-end gap-3">
                      <div className="min-w-[200px] flex-1">
                        <Label className="text-[11px] font-semibold text-sky-900">
                          Plazo autorizado <span className="text-rose-600">*</span>
                        </Label>
                        {(sel.producto_plazos_permitidos || []).length > 0 ? (
                          <Select value={plazoAut} onValueChange={setPlazoAut} disabled={busy}>
                            <SelectTrigger className="mt-1 bg-white"
                              data-testid="comite-plazo-autorizado">
                              <SelectValue placeholder="Elige el plazo autorizado..." />
                            </SelectTrigger>
                            <SelectContent>
                              {(sel.producto_plazos_permitidos || []).map((p) => (
                                <SelectItem key={p} value={String(p)}
                                  data-testid={`comite-plazo-opcion-${p}`}>
                                  {formatearPlazo(p, sel.periodicidad)}
                                  {Number(p) === Number(sel.plazo) ? " · solicitado" : ""}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <input type="number" min="1" step="1" disabled={busy}
                            data-testid="comite-plazo-autorizado"
                            className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                            value={plazoAut} onChange={(e) => setPlazoAut(e.target.value)} />
                        )}
                      </div>
                      <div className="text-[11px] text-sky-900">
                        <p>Solicitado: <b data-testid="comite-plazo-solicitado-ref">
                          {formatearPlazo(sel.plazo, sel.periodicidad)}</b></p>
                        <p className="mt-0.5">
                          Periodicidad: {sel.producto_periodicidad || sel.periodicidad || "—"} (no cambia)
                        </p>
                      </div>
                    </div>
                    {Number(plazoAut) > 0 && Number(sel.plazo) > 0
                      && Number(plazoAut) !== Number(sel.plazo) && (
                      <p className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900"
                        data-testid="comite-plazo-aviso-cambio">
                        Cambias el plazo de <b>{formatearPlazo(sel.plazo, sel.periodicidad)}</b> a{" "}
                        <b>{formatearPlazo(plazoAut, sel.periodicidad)}</b>: el <b>comentario es
                        obligatorio</b> para explicar el cambio. La tasa, la comisión, el IVA y la
                        periodicidad <b>no cambian</b>.
                      </p>
                    )}
                    <VistaPreviaComite solicitudId={sel.id} montoAut={montoAut} plazoAut={plazoAut} />
                    </>)}
                    <p className="mt-2 text-[11px] text-sky-800">
                      Obligatorio para <b>Aprobado</b> y <b>Condicionado</b>; no se pide en
                      Rechazado, Más documentos, Nueva consulta ni Revisión PLD. El monto debe
                      ser mayor a $0 y nunca superior al solicitado. Al decidir quedan{" "}
                      <b>sellados</b>: de ahí salen la comisión por apertura y la tabla de
                      amortización, con el <b>plazo que autorices</b>.
                    </p>
                  </div>
                )}
                <Textarea data-testid="comite-comentario"
                  placeholder="Comentario del Comité (queda en el historial)..."
                  value={comentario} onChange={(e) => setComentario(e.target.value)} />
                <div className="mt-3 flex flex-wrap gap-2">
                  {DECISIONES.map(([d, tip]) => (
                    <Button key={d} title={tip} variant="outline" size="sm"
                      disabled={busy || !puedeDecidir}
                      data-testid={`comite-decidir-${d.replace(/\s+/g, "-")}`}
                      onClick={() => decidir(d)}>
                      {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}{d}
                    </Button>
                  ))}
                </div>
              </div>

              {data?.puede_asignar && (
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="mb-2 font-heading text-sm font-semibold text-slate-800">
                    Asignación {sel.comite_asignado_nombre ? `· actual: ${sel.comite_asignado_nombre}` : "· sin asignar"}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Select value={destino} onValueChange={setDestino}>
                      <SelectTrigger className="w-64" data-testid="comite-select-analista">
                        <SelectValue placeholder="Elige a quién asignar..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(data?.analistas || []).map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" data-testid="comite-reasignar-btn" disabled={busy || !destino}
                      onClick={reasignar} className="bg-accent hover:bg-forest-700">Reasignar</Button>
                  </div>
                </div>
              )}

              <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="comite-historial">
                <p className="mb-2 font-heading text-sm font-semibold text-slate-800">
                  Historial de decisiones ({(sel.comite_historial || []).length})
                </p>
                {(sel.comite_historial || []).length === 0 && (
                  <p className="text-xs text-slate-400">Sin decisiones registradas.</p>
                )}
                <ol className="space-y-2">
                  {(sel.comite_historial || []).map((h, i) => (
                    <li key={h.id || i} data-testid={`comite-historial-${i}`}
                      className="rounded-md border border-slate-100 bg-slate-50 p-2 text-[13px]">
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusBadge value={h.decision} />
                        <span className="text-slate-600">{h.usuario}</span>
                        <span className="text-slate-400">· {h.fecha}</span>
                        <MarcaMigracionComite h={h} i={i} />
                        {h.autorizacion_propia && (
                          <span data-testid={`comite-marca-propia-${i}`}
                            className="rounded bg-amber-600 px-1.5 py-0.5 text-[10px] font-medium text-white">
                            {h.marca || "Autorización propia"}
                          </span>
                        )}
                      </div>
                      {h.comentarios && <p className="mt-1 text-slate-700">{h.comentarios}</p>}
                      {h.justificacion && (
                        <p className="mt-1 text-amber-900">Justificación: {h.justificacion}</p>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
